import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DateTime } from "luxon";

import { Context } from "../../context/context";
import { Card, PageSpinner } from "../../components/ui";
import { ChevronRightIcon, SyncIcon, SyncProblemIcon } from "../../components/ui/icons";
import { adherenceOver, dosesScheduledToday, timeUntil, upcomingDoses, weekOverWeek } from "../../utilities/adherence";
import { joinList } from "../../utilities/utils";

const greeting = (zone) => {
  const hour = DateTime.now().setZone(zone).hour;
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const StatCard = ({ label, value, note, tone = "ink", className = "" }) => (
  <Card className={`p-5 ${className}`}>
    <p className="mb-1 text-xs text-gray-500">{label}</p>
    <p className={`font-mono text-2xl font-bold ${{ ink: "text-primary-900", good: "text-teal-600", warning: "text-amber-600" }[tone]}`}>{value}</p>
    {note && <p className="mt-0.5 text-[11px] text-gray-500">{note}</p>}
  </Card>
);

const Dashboard = () => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [adherence, setAdherence] = useState(null);

  const { state } = useContext(Context);
  const { user } = state;

  const { deviceId } = useParams();
  const navigate = useNavigate();

  const loadDeviceInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`/app-api/device/${deviceId}/dashboard`);
      if (data) setDeviceInfo(data);
    } catch (err) {
      console.error(err);
    }
  }, [deviceId]);

  const loadAdherence = useCallback(async () => {
    try {
      const { data } = await axios.get(`/app-api/device/${deviceId}/adherence`);
      if (data) setAdherence(data);
    } catch (err) {
      console.error(err);
    }
  }, [deviceId]);

  useEffect(() => {
    loadDeviceInfo();
    loadAdherence();
  }, [loadDeviceInfo, loadAdherence]);

  if (!deviceInfo) return <PageSpinner />;

  const zone = deviceInfo.deviceTimezone?.timezoneValue || DateTime.local().zoneName;
  const days = adherence?.days || [];
  const today = days[days.length - 1];
  const upcoming = upcomingDoses(deviceInfo.deviceInfo, zone);
  const next = upcoming[0] || null;
  const nextIn = next ? timeUntil(next.code, zone) : null;
  const week = adherenceOver(days, 7);
  const delta = weekOverWeek(days);

  const dosesToday = dosesScheduledToday(deviceInfo.deviceInfo);
  const takenToday = today?.metrics.pillsTaken || 0;

  return (
    <>
      <div className="mb-5 grid gap-5 lg:grid-cols-3">
        <div className="flex flex-col justify-center rounded-2xl p-6 lg:col-span-2" style={{ background: "linear-gradient(120deg,#1d9e75,#24b589)" }}>
          <p className="mb-1 text-xl font-bold text-white">
            {greeting(zone)}
            {user?.displayName ? `, ${user.displayName.split(" ")[0]}` : ""}
          </p>
          <p className="text-sm text-teal-50">
            {adherence ? `${takenToday} of ${dosesToday} ${dosesToday === 1 ? "dose" : "doses"} taken so far today` : "Loading today's doses…"}
            {next && ` · Next dose in ${nextIn.inHours ? `${nextIn.inHours}h ` : ""}${nextIn.inMinutes}m`}
          </p>
        </div>

        <Card className="flex flex-col justify-center p-5">
          <p className="mb-1 text-xs text-gray-500">7-Day Adherence</p>
          <p className="font-mono text-3xl font-bold text-primary-700">{week.pct === null ? "—" : `${week.pct}%`}</p>
          {delta !== null && delta !== 0 && (
            <p className={`mt-1 text-[11px] font-medium ${delta > 0 ? "text-teal-600" : "text-amber-600"}`}>
              {delta > 0 ? "↑" : "↓"} {Math.abs(delta)}% vs. last week
            </p>
          )}
        </Card>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3 nav:gap-5">
        <StatCard label="Doses Taken Today" value={today ? today.metrics.pillsTaken : "—"} />
        <StatCard label="Doses Missed Today" value={today ? today.metrics.pillsMissed : "—"} tone={today?.metrics.pillsMissed ? "warning" : "ink"} />
        <StatCard
          className="col-span-2 sm:col-span-1"
          label="Next Alarm"
          value={next ? next.label : "—"}
          note={next ? `Box ${joinList(next.doses.map((d) => d.boxNumber))}` : "No further doses today"}
        />
      </div>

      <Card className={`mb-5 flex items-center gap-4 p-5 ${deviceInfo.sync?.status ? "border-teal-100 bg-teal-50" : "border-red-100 bg-red-50"}`}>
        <span
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white ${
            deviceInfo.sync?.status ? "text-teal-600" : "text-red-600"
          }`}
        >
          {deviceInfo.sync?.status ? <SyncIcon className="h-5 w-5" /> : <SyncProblemIcon className="h-5 w-5" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-primary-900">{deviceInfo.sync?.status ? "Your device is synced" : "Please sync your device"}</p>
          <p className="text-xs text-gray-600">
            {deviceInfo.sync?.status
              ? "Dose events are arriving from the device as they happen."
              : "Recent dose events may be missing until the device reconnects."}
          </p>
        </div>
      </Card>

      <Card className="p-5 nav:p-6">
        <div className="mb-5 flex items-center justify-between gap-4">
          <p className="text-sm font-bold text-primary-900">Upcoming Today</p>
          <button
            onClick={() => navigate(`/device/${deviceId}/adherence`)}
            className="flex items-center gap-1 text-xs font-semibold text-primary-600"
          >
            Adherence history
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>

        {!adherence ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading today's doses…</p>
        ) : upcoming.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">
            {dosesToday === 0
              ? "No alarms are set yet. Add a pill to a compartment to see today's doses here."
              : "No more doses today — check back tomorrow."}
          </p>
        ) : (
          <div className="relative space-y-7 pl-7">
            <span className="absolute bottom-1.5 left-[7px] top-1.5 w-0.5 bg-[#e5e9f0]" />
            {upcoming.map((slot) => (
              <div key={slot.code} className="relative">
                <span
                  className="absolute -left-7 top-1 h-4 w-4 rounded-full border-[3px] border-white bg-primary-500"
                  style={{ boxShadow: "0 0 0 2px #d9e7f6" }}
                />
                <p className="text-sm font-semibold text-primary-900">
                  {slot.label} · Box {joinList(slot.doses.map((d) => d.boxNumber))}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">{slot.doses.map((d) => d.medicineName).join(" · ")}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
};

export default Dashboard;
